import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, Check, X, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking", paymentStatus: null, amount: 0 });
  const [pollCount, setPollCount] = useState(0);
  const meta = (() => { try { return JSON.parse(sessionStorage.getItem("last_booking") || "null"); } catch { return null; } })();

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let cancelled = false;
    let count = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setState({ status: data.status, paymentStatus: data.payment_status, amount: data.amount });
        if (data.payment_status === "paid" || data.status === "completed") return;
        if (count >= MAX_POLLS) { setState((s) => ({ ...s, status: "timeout" })); return; }
        count += 1; setPollCount(count);
        setTimeout(poll, 2000);
      } catch {
        setState({ status: "error" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.paymentStatus === "paid" || state.status === "completed";
  const failed = state.status === "error" || state.status === "timeout";

  return (
    <div className="min-h-screen bg-background text-foreground grain flex items-center justify-center px-6">
      <div className="w-full max-w-lg bg-card border gold-border clip-frame p-10 text-center" data-testid="payment-success-page">
        {!paid && !failed && (
          <>
            <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-foreground mb-3">Confirming your payment…</h1>
            <p className="text-muted-foreground font-light">Hang tight, this usually takes a few seconds. ({pollCount}/{MAX_POLLS})</p>
          </>
        )}
        {paid && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/15 flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-primary" />
            </div>
            <h1 className="font-serif text-4xl text-foreground mb-3" data-testid="payment-success-title">Booking confirmed</h1>
            <p className="text-muted-foreground font-light mb-2">
              {meta ? <>Thanks, {meta.name?.split(" ")[0]} — your <span className="text-primary">{meta.service_name}</span> is booked for <span className="text-foreground">{meta.date}</span> at <span className="text-foreground">{meta.time_slot}</span>.</> : "Your booking is confirmed."}
            </p>
            <p className="text-muted-foreground font-light mb-8">A confirmation email is on its way. Your ${(state.amount/100).toFixed(2)} deposit will be applied to your final bill.</p>
            <Link to="/" data-testid="payment-success-home" className="inline-block px-8 py-3 bg-primary text-primary-foreground text-sm tracking-wide hover:bg-primary/90 transition-colors clip-frame">
              Back to site
            </Link>
          </>
        )}
        {failed && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-destructive/15 flex items-center justify-center mb-6">
              <X className="w-8 h-8 text-destructive" />
            </div>
            <h1 className="font-serif text-3xl text-foreground mb-3">Payment not confirmed</h1>
            <p className="text-muted-foreground font-light mb-8">
              We couldn&apos;t verify your payment. If you completed checkout, please give us a call at <a className="text-primary" href="tel:+14062136881">406-213-6881</a> — we&apos;ll sort it out.
            </p>
            <Link to="/#book" className="inline-flex items-center gap-2 px-8 py-3 border border-primary/40 text-primary text-sm tracking-wide hover:bg-primary hover:text-primary-foreground transition-colors clip-frame">
              <ArrowLeft className="w-4 h-4" /> Try again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
